import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { ClockCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import InputFilterItem from "../../../../delivery/components/InputFilterItem";
import DateRangeFilterItem from "../../../../delivery/components/DateRangeFilterItem";
import { AccountTransactionFlowFilterType } from '../graphql/types/AccountTransactionFlowType';
import { ChannelSelectFilter } from '../../../../../components/settlement/ChannelSelectFilter';
import { formatTimeRangeToISO } from '../../../../../utils/dateTimeUtils';
import { CREATE_ACCOUNT_TRANSACTION_FLOW_DOWNLOAD_TASK, AccountTransactionFlowDownloadTaskResponse } from '../graphql/mutations/createAccountTransactionFlowDownloadTask';
import ParcelsDownloadModal from '../../components/ParcelsDownloadModal';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

interface AccountTransactionFlowFilterProps {
    filters: AccountTransactionFlowFilterType;
    setFilters: (filters: AccountTransactionFlowFilterType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
}

const AccountTransactionFlowFilter: React.FC<AccountTransactionFlowFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
}) => {
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间

    // 下载完成模态框状态
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');

    // 使用mutation来创建下载任务
    const [createAccountTransactionFlowDownloadTask] = useMutation<AccountTransactionFlowDownloadTaskResponse>(CREATE_ACCOUNT_TRANSACTION_FLOW_DOWNLOAD_TASK);

    // 从localStorage获取上次下载时间，页面刷新后仍然有效
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('accountTransactionFlowDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };

    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('accountTransactionFlowDownloadLastTime', time.toString());
    };

    // 冷却时间倒计时（页面刷新后仍然有效）
    useEffect(() => {
        const checkCooldown = () => {
            const lastTime = getLastDownloadTime();
            if (lastTime > 0) {
                const currentTime = Date.now();
                const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownRemaining(remainingSeconds);

                if (remaining <= 0) {
                    setCooldownRemaining(0);
                    setDownloadLoading(false);
                    localStorage.removeItem('accountTransactionFlowDownloadLastTime'); // 清理过期数据
                }
            } else {
                setCooldownRemaining(0);
            }
        };

        // 立即检查一次（页面刷新后）
        checkCooldown();
        // 启动定时器，每秒更新一次
        const interval = setInterval(checkCooldown, 1000);
        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);

    // 处理时间变化的函数
    const handleTransactionTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({ ...filters, transactionTime: dates });
    };

    // 构建查询参数
    const buildQueryFilters = (filters: AccountTransactionFlowFilterType) => {
        const timeRanges = {
            transactionTime: formatTimeRangeToISO(filters.transactionTime),
        };

        return {
            ...filters,
            ...timeRanges
        };
    };

    // Download handler function
    const handleDownloadAccountTransactionFlow = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        // Check cooldown time
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again to avoid duplicate requests`);
            return;
        }
        // Check if already loading
        if (downloadLoading) {
            message.warning('Report is being generated, please do not click repeatedly');
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime); // Store to localStorage
            // 使用与filter按钮完全相同的参数构建逻辑
            const queryFilters = buildQueryFilters(filters);
            const response = await createAccountTransactionFlowDownloadTask({
                variables: {
                    filters: queryFilters,
                }
            });
            if (response.data?.createAccountTransactionFlowDownloadTask) {
                setGeneratedFileName(response.data?.createAccountTransactionFlowDownloadTask);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start report generation task');
            }
        } catch (error) {
            console.error('Create AccountTransactionFlow Download Task Failed:', error);
            message.error('Create AccountTransactionFlow Download Task Failed, Please Try Again Later');
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="mb-6">
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mb-4">
                <ChannelSelectFilter
                    label="客户名称"
                    value={filters.clientId ?? undefined}
                    onChange={(value: string | undefined) => setFilters({...filters, clientId: value})}
                    spanClassName="w-[9em]"
                />
                <InputFilterItem
                    label="账户ID"
                    value={filters.virtualAccountId || undefined}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, virtualAccountId: e.target.value || undefined })}
                    spanClassName="w-[9em]"
                />
                <DateRangeFilterItem
                    label="交易时间"
                    value={filters.transactionTime}
                    onChange={handleTransactionTimeChange}
                    spanClassName="w-[9em]"
                />
            </div>

            <div className="mb-4 flex justify-between">
                <div className="space-x-4">
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_ACCOUNT_STATEMENT]}>
                        <Button
                            type="primary"
                            onClick={handleDownloadAccountTransactionFlow}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            icon={cooldownRemaining > 0 ? <ClockCircleOutlined/> : <DownloadOutlined/>}
                        >
                            {cooldownRemaining > 0
                                ? `Download ${cooldownRemaining}s`
                                : 'Download'
                            }
                        </Button>
                    </HasResource>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_ACCOUNT_STATEMENT]}>
                        <Button type="primary" onClick={handleFilter}>
                            Filter
                        </Button>
                    </HasResource>
                    <Button onClick={handleResetFilters}>
                        Reset
                    </Button>
                </div>
            </div>

            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default AccountTransactionFlowFilter;

